import 'ol/ol.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import OlMap from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import { LineString, MultiPolygon, Point, Polygon } from 'ol/geom';
import { Circle as CircleStyle, Fill, Icon, Stroke, Style } from 'ol/style';
import { ScaleLine, defaults as defaultControls } from 'ol/control';
import { boundingExtent } from 'ol/extent';

import { theme } from 'antd';
import { getMaptilerKey } from '@utils/api';
import { getBaseMaps } from '@routes/userRoutes/projects/singleProject/components/layers/baseMap/constants';
import { coverageSeries, flightAt, pathLengthSeries, pathUpTo, sweptUpTo } from './flightModel';
import { Chip, Chips } from './CountPanel';

/**
 * The flight next to the video: the portal's base map (MapTiler satellite, OpenStreetMap without a key), the drone at
 * its position turned to the camera heading, the path flown so far, the camera footprint of the current frame with
 * its top edge (the direction the camera looks) drawn bright, the ground covered so far, and each counted animal
 * where it stood, appearing the frame it is counted. Clicking an animal seeks the video there.
 * A custom component: antd has no map; the toggles are the count panel's chips on a glass panel.
 */

// canvas colours on the map (OpenLayers styles, not CSS); calf-sized matches the count panel's histogram
const CALF = '#90d7ad';
const WHITE = '#ffffff';
const PULSE_FRAMES = 20;

const droneIcon = green =>
	`data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
<g fill="none" stroke="#f5f5f5" stroke-width="2.2"><path d="M12 12 L32 32 M32 12 L12 32"/>
<circle cx="12" cy="12" r="5.5"/><circle cx="32" cy="12" r="5.5"/><circle cx="12" cy="32" r="5.5"/><circle cx="32" cy="32" r="5.5"/></g>
<path d="M22 2 L27 10 L17 10 Z" fill="${green}"/><circle cx="22" cy="22" r="4.5" fill="${green}" stroke="#121412" stroke-width="1.5"/></svg>`)}`;

const dot = (color, r, ring) =>
	new Style({
		image: new CircleStyle({
			radius: r,
			fill: new Fill({ color }),
			stroke: new Stroke({ color: ring || 'rgba(0,0,0,0.65)', width: ring ? 2 : 1 }),
		}),
	});

const mapStyles = green => ({
	swept: new Style({ fill: new Fill({ color: green }) }),
	fullPath: new Style({
		stroke: new Stroke({ color: 'rgba(245,245,245,0.35)', width: 2, lineDash: [4, 6] }),
	}),
	trail: new Style({ stroke: new Stroke({ color: 'rgba(245,245,245,0.95)', width: 2.5 }) }),
	footprint: new Style({
		fill: new Fill({ color: 'rgba(32,172,107,0.18)' }),
		stroke: new Stroke({ color: green, width: 2 }),
	}),
	view: new Style({ stroke: new Stroke({ color: WHITE, width: 4, lineCap: 'round' }) }),
	dots: {
		adult: dot(green, 3.5),
		calf: dot(CALF, 3.5),
		pulseAdult: dot(green, 6, WHITE),
		pulseCalf: dot(CALF, 6, WHITE),
		selected: dot(WHITE, 7, green),
	},
	icon: droneIcon(green),
});

const tk = ({ theme: t }) => t.token;

const Wrap = styled.div`
	position: relative;
	height: 100%;
	border-radius: ${p => tk(p).borderRadiusLG}px;
	overflow: hidden;
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
	background: ${p => tk(p).colorBgBase};
	.ol-scale-line {
		left: auto;
		right: ${p => tk(p).marginSM}px;
		bottom: ${p => tk(p).marginSM}px;
		background: ${p => tk(p).panelGlassBg};
	}
	.ol-zoom {
		top: auto;
		left: auto;
		right: ${p => tk(p).marginSM}px;
		bottom: ${p => tk(p).marginSM + tk(p).controlHeight}px;
	}
	.ol-attribution {
		font-size: ${p => tk(p).fontSizeXXS}px;
	}
`;

const MapDiv = styled.div`
	position: absolute;
	inset: 0;
`;

const Hud = styled.div`
	position: absolute;
	top: ${p => tk(p).marginSM}px;
	left: ${p => tk(p).marginSM}px;
	right: ${p => tk(p).marginSM}px;
	display: flex;
	flex-wrap: wrap;
	gap: ${p => tk(p).marginXS}px;
	align-items: flex-start;
	justify-content: space-between;
	pointer-events: none;
	z-index: 2;
	& > * {
		pointer-events: auto;
	}
`;

const Stats = styled.div`
	display: grid;
	grid-template-columns: auto auto;
	gap: ${p => `${tk(p).marginXXS}px ${tk(p).marginSM}px`};
	padding: ${p => `${tk(p).paddingXS}px ${tk(p).paddingSM}px`};
	max-width: 46%;
	border-radius: ${p => tk(p).borderRadiusLG}px;
	background: ${p => tk(p).panelGlassBg};
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextSecondary};
	b {
		color: ${p => tk(p).colorText};
		font-variant-numeric: tabular-nums;
		font-weight: ${p => tk(p).fontWeightStrong};
		text-align: right;
	}
`;

// how the positions were worked out, as the last line of the stats
const Caption = styled.div`
	grid-column: 1 / -1;
	margin-top: ${p => tk(p).marginXXS}px;
	font-size: ${p => tk(p).fontSizeXXS}px;
	color: ${p => tk(p).colorTextTertiary};
`;

const Controls = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: ${p => tk(p).marginXS}px;
	padding: ${p => tk(p).paddingXS}px;
	border-radius: ${p => tk(p).borderRadiusLG}px;
	background: ${p => tk(p).panelGlassBg};
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
`;

// the count panel's chips, so the map's toggles read like its mode switch
const ChipGroup = ({ label, options, value, onChange }) => (
	<Chips role="tablist" aria-label={label}>
		{options.map(o => (
			<Chip
				key={o.value}
				type="button"
				role="tab"
				aria-selected={o.value === value}
				$active={o.value === value}
				onClick={() => onChange(o.value)}>
				{o.label}
			</Chip>
		))}
	</Chips>
);

const fmtArea = m2 => (m2 >= 10000 ? `${(m2 / 10000).toFixed(2)} ha` : `${Math.round(m2)} m²`);

const FlightMap = ({ flight, animals = [], frame, projectUuid, onSeekFrame, compact = false }) => {
	const divRef = useRef(null);
	const mapRef = useRef(null);
	const layers = useRef({});
	const selectedRef = useRef(null);
	const frameRef = useRef(frame);
	const seekRef = useRef(onSeekFrame);
	seekRef.current = onSeekFrame;
	const baseRef = useRef(null);
	const [follow, setFollow] = useState(false);
	const [base, setBase] = useState('satellite');
	const { token } = theme.useToken();
	const styles = useMemo(() => mapStyles(token.colorPrimary), [token.colorPrimary]);

	const { data: maptilerKey } = useQuery({
		queryKey: ['maptiler-key', projectUuid],
		queryFn: () => getMaptilerKey(projectUuid),
		enabled: !!projectUuid,
		staleTime: Infinity,
		retry: 1,
	});
	const baseMaps = useMemo(() => getBaseMaps(maptilerKey), [maptilerKey]);

	const coverage = useMemo(() => coverageSeries(flight?.doc), [flight]);
	const flown = useMemo(() => pathLengthSeries(flight?.doc), [flight]);

	// the map and its layers, once per flight
	useEffect(() => {
		if (!flight || !divRef.current) return undefined;
		const src = () => new VectorSource();
		const L = {
			base: new TileLayer({ zIndex: 0, preload: Infinity }),
			swept: new VectorLayer({
				source: src(),
				style: styles.swept,
				opacity: 0.22,
				zIndex: 1,
			}),
			path: new VectorLayer({ source: src(), zIndex: 2 }),
			footprint: new VectorLayer({ source: src(), zIndex: 3 }),
			animals: new VectorLayer({ source: src(), zIndex: 4 }),
			drone: new VectorLayer({ source: src(), zIndex: 5 }),
		};
		const fullPath = new Feature(new LineString(flight.pos));
		fullPath.setStyle(styles.fullPath);
		const trail = new Feature(new LineString([]));
		trail.setStyle(styles.trail);
		L.path.getSource().addFeatures([fullPath, trail]);
		const fp = new Feature(new Polygon([]));
		fp.setStyle(styles.footprint);
		const viewEdge = new Feature(new LineString([]));
		viewEdge.setStyle(styles.view);
		L.footprint.getSource().addFeatures([fp, viewEdge]);
		const swept = new Feature(new MultiPolygon([]));
		L.swept.getSource().addFeature(swept);
		const drone = new Feature(new Point(flight.pos[0]));
		L.drone.getSource().addFeature(drone);
		if (baseRef.current) L.base.setSource(baseRef.current());
		L.animals.setStyle(feature => {
			const f = frameRef.current;
			const at = feature.get('frame');
			if (at > f) return null;
			if (selectedRef.current === feature.get('track')) return styles.dots.selected;
			const calf = feature.get('calf');
			if (f - at < PULSE_FRAMES) return calf ? styles.dots.pulseCalf : styles.dots.pulseAdult;
			return calf ? styles.dots.calf : styles.dots.adult;
		});

		const extentPts = [...flight.pos, ...flight.fp.filter(Boolean).flat()];
		const map = new OlMap({
			target: divRef.current,
			layers: Object.values(L),
			controls: defaultControls({
				zoom: !compact,
				rotate: false,
				attributionOptions: { collapsible: true },
			}).extend([new ScaleLine({ units: 'metric' })]),
			view: new View({ maxZoom: 22 }),
		});
		const extent = boundingExtent(extentPts);
		// padding scales with the map, leaving room for the stats at the top
		const fitWhole = () => {
			const size = map.getSize();
			if (!size || size[0] < 2 || size[1] < 2) return;
			const [w, h] = size;
			map.getView().fit(extent, {
				padding: [h * (compact ? 0.22 : 0.3), w * 0.08, h * 0.1, w * 0.08],
				maxZoom: 20,
				size,
			});
		};
		map.on('singleclick', e => {
			const hit = map.forEachFeatureAtPixel(e.pixel, f => f, {
				layerFilter: l => l === L.animals,
				hitTolerance: 6,
			});
			if (!hit) return;
			selectedRef.current = hit.get('track');
			L.animals.changed();
			seekRef.current?.(hit.get('frame'), hit.get('track'));
		});
		map.on('pointermove', e => {
			const over = map.hasFeatureAtPixel(e.pixel, {
				layerFilter: l => l === L.animals,
				hitTolerance: 6,
			});
			map.getTargetElement().style.cursor = over ? 'pointer' : '';
		});
		// the whole flight stays fitted as the map is laid out and resized, until the user pans or zooms
		let userMoved = false;
		map.on('pointerdrag', () => {
			userMoved = true;
		});
		map.getViewport().addEventListener('wheel', () => {
			userMoved = true;
		});
		const fitWhenSized = () => {
			if (!userMoved || !map.getView().isDef()) fitWhole();
		};
		map.on('change:size', fitWhenSized);
		const ro = new ResizeObserver(() => {
			map.updateSize();
			fitWhenSized();
		});
		ro.observe(divRef.current);
		// a map in a slot that gets its size late (the corner view) may never see a size change: poll until fitted
		const poll = setInterval(() => {
			if (map.getView().isDef()) {
				clearInterval(poll);
				return;
			}
			map.updateSize();
			fitWhenSized();
		}, 250);
		layers.current = {
			...L,
			trail,
			fp,
			viewEdge,
			swept,
			drone,
			sweptAt: -1,
			fitWhole,
			fitWhenSized,
		};
		mapRef.current = map;
		return () => {
			clearInterval(poll);
			ro.disconnect();
			map.setTarget(null);
			mapRef.current = null;
		};
	}, [flight, styles, compact]);

	// the counted animals; a new sensitivity changes them without rebuilding the map
	useEffect(() => {
		const layer = layers.current.animals;
		if (!layer) return;
		const source = layer.getSource();
		source.clear();
		source.addFeatures(
			animals.map(
				a =>
					new Feature({
						geometry: new Point(a.at),
						frame: a.frame,
						calf: a.calf,
						track: a.track,
					})
			)
		);
	}, [animals, flight, styles, compact]);

	// base map: satellite when the MapTiler key is there, else the open street map
	useEffect(() => {
		const cfg = (maptilerKey ? baseMaps[base] : null) || baseMaps.standard;
		baseRef.current = cfg?.xyz?.url
			? () => new XYZ({ ...cfg.xyz, crossOrigin: 'anonymous' })
			: null;
		if (layers.current.base && baseRef.current)
			layers.current.base.setSource(baseRef.current());
	}, [baseMaps, base, maptilerKey]);

	// every frame: drone, footprint, trail, swept ground and which animals have been counted
	useEffect(() => {
		frameRef.current = frame;
		const L = layers.current;
		if (!flight || !L.drone) return;
		const now = flightAt(flight, frame);
		L.drone.setStyle(
			new Style({
				image: new Icon({
					src: styles.icon,
					scale: 0.75,
					rotation: ((now.heading || 0) * Math.PI) / 180,
					rotateWithView: true,
				}),
			})
		);
		L.drone.getGeometry().setCoordinates(now.position);
		L.trail.getGeometry().setCoordinates(pathUpTo(flight, frame));
		if (now.footprint) {
			L.fp.getGeometry().setCoordinates([[...now.footprint, now.footprint[0]]]);
			L.viewEdge.getGeometry().setCoordinates([now.footprint[0], now.footprint[1]]);
		}
		if (now.sample !== L.sweptAt) {
			L.swept.getGeometry().setCoordinates(sweptUpTo(flight, frame).map(r => [[...r, r[0]]]));
			L.sweptAt = now.sample;
		}
		L.animals.changed();
		if (follow) mapRef.current?.getView().setCenter(now.position);
	}, [flight, frame, follow, styles]);

	if (!flight) return null;
	const now = flightAt(flight, frame);
	const counted = animals.filter(a => a.frame <= frame).length;

	const takeoff = flight?.doc?.height_ref === 'takeoff';
	// a negative height above take-off: the drone took off above the ground it filmed
	const belowTakeoff = takeoff && (flight?.alt || []).some(a => a != null && a < 0);
	return (
		<Wrap>
			<MapDiv ref={divRef} />
			{compact ? (
				<Hud>
					<Stats>
						<span>Flown</span>
						<b>{Math.round(flown[now.sample] || 0)} m</b>
					</Stats>
				</Hud>
			) : (
				<>
					<Hud>
						<Stats>
							<span>On the map</span>
							<b>{counted}</b>
							{flight.hasFootprint && (
								<>
									<span>Ground covered</span>
									<b>{fmtArea(coverage[now.sample] || 0)}</b>
								</>
							)}
							<span>Flown</span>
							<b>{Math.round(flown[now.sample] || 0)} m</b>
							{now.alt != null && (
								<>
									<span>
										{!takeoff
											? 'Height'
											: now.alt < 0
												? 'Below take-off'
												: 'Height above take-off'}
									</span>
									<b>{Math.round(Math.abs(now.alt))} m</b>
								</>
							)}
							<Caption>
								{flight.hasFootprint
									? takeoff
										? 'Positions use the height above the take-off point, so they are exact on flat ground'
										: 'Positions use the height the drone reports above the ground'
									: belowTakeoff
										? 'The drone flew below its take-off point, so its height above the ground is unknown and the camera footprint is not drawn'
										: 'The camera footprint needs the height above the ground, which this video does not have'}
							</Caption>
						</Stats>
						<Controls>
							<ChipGroup
								label="Map view"
								value={follow ? 'follow' : 'whole'}
								onChange={v => {
									setFollow(v === 'follow');
									if (v === 'whole') layers.current.fitWhole?.();
								}}
								options={[
									{ label: 'Whole flight', value: 'whole' },
									{ label: 'Follow drone', value: 'follow' },
								]}
							/>
							{maptilerKey && (
								<ChipGroup
									label="Base map"
									value={base}
									onChange={setBase}
									options={[
										{ label: 'Satellite', value: 'satellite' },
										{ label: 'Terrain', value: 'terrain' },
									]}
								/>
							)}
						</Controls>
					</Hud>
				</>
			)}
		</Wrap>
	);
};

export default FlightMap;
